import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  LinearProgress,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Replay as ReplayIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as RunningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { executionService } from '../services/execution.service';
import { Execution, NodeExecution } from '../types/workflow.types';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG: Record<string, { color: 'success' | 'error' | 'warning' | 'info' | 'default'; label: string; icon?: React.ReactNode }> = {
  completed: { color: 'success', label: 'Completed', icon: <CheckCircleIcon fontSize="small" /> },
  failed:    { color: 'error',   label: 'Failed',    icon: <ErrorIcon fontSize="small" /> },
  running:   { color: 'info',    label: 'Running',   icon: <RunningIcon fontSize="small" /> },
  pending:   { color: 'warning', label: 'Pending',   icon: <PendingIcon fontSize="small" /> },
  cancelled: { color: 'default', label: 'Cancelled' },
};

function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { color: 'default' as const, label: status };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function formatDuration(duration: string | number | undefined) {
  if (!duration) return '-';
  if (typeof duration === 'number') {
    if (duration < 60) return `${duration.toFixed(1)}s`;
    const m = Math.floor(duration / 60);
    const s = Math.round(duration % 60);
    return `${m}m ${s}s`;
  }
  if (typeof duration === 'string') {
    try {
      const parts = duration.split(':');
      if (parts.length === 3) {
        const [h, m, s] = parts.map(Number);
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${Math.round(s)}s`;
        return `${s.toFixed(1)}s`;
      }
    } catch { /* fall through */ }
  }
  return String(duration);
}

function OutputBlock({ data, label }: { data: any; label: string }) {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
  return (
    <Box mt={1}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Box
        component="pre"
        sx={{
          m: 0, mt: 0.5, p: 1,
          bgcolor: 'grey.50', borderRadius: 1,
          fontSize: '0.72rem', overflowX: 'auto',
          maxHeight: 200, border: '1px solid', borderColor: 'divider',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {JSON.stringify(data, null, 2)}
      </Box>
    </Box>
  );
}

export const ExecutionsPage: React.FC = () => {
  const { user } = useAuth();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Detail dialog
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null);
  const [selectedNodeExecutions, setSelectedNodeExecutions] = useState<NodeExecution[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes(perm);
  };
  const canView   = hasPerm('execution.view');
  const canCancel = hasPerm('execution.cancel');

  // ── List loading ────────────────────────────────────────────────────────────
  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { page: page + 1, page_size: rowsPerPage };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await executionService.getExecutions(params);
      setExecutions(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchQuery]);

  useEffect(() => { loadExecutions(); }, [loadExecutions]);

  // ── Detail dialog helpers ───────────────────────────────────────────────────
  const fetchDetail = useCallback(async (id: string) => {
    try {
      const [fullExec, nodeExecs] = await Promise.all([
        executionService.getExecution(id),
        executionService.getNodeExecutions(id),
      ]);
      setSelectedExecution(fullExec);
      setSelectedNodeExecutions(nodeExecs);
      return fullExec;
    } catch {
      return null;
    }
  }, []);

  const startPolling = useCallback((id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const exec = await fetchDetail(id);
      if (!exec || !['pending', 'running'].includes(exec.status)) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        loadExecutions(); // refresh list when it finishes
      }
    }, 3000);
  }, [fetchDetail, loadExecutions]);

  const openDetails = async (execution: Execution) => {
    // Stop any previous poll
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setSelectedExecution(execution);
    setSelectedNodeExecutions([]);
    setDetailDialogOpen(true);
    setDetailsLoading(true);
    const full = await fetchDetail(execution.id);
    setDetailsLoading(false);
    if (full && ['pending', 'running'].includes(full.status)) {
      startPolling(full.id);
    }
  };

  const closeDetails = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setDetailDialogOpen(false);
    setSelectedExecution(null);
    setSelectedNodeExecutions([]);
  };

  // Cleanup poll on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleCancel = async (execution: Execution) => {
    if (!canCancel) { enqueueSnackbar('No permission to cancel executions', { variant: 'error' }); return; }
    try {
      await executionService.cancelExecution(execution.id);
      enqueueSnackbar('Execution cancelled', { variant: 'success' });
      if (selectedExecution?.id === execution.id) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        await fetchDetail(execution.id);
      }
      loadExecutions();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to cancel', { variant: 'error' });
    }
  };

  const handleRetry = async (execution: Execution) => {
    if (!canCancel) { enqueueSnackbar('No permission to retry executions', { variant: 'error' }); return; }
    try {
      await executionService.retryExecution(execution.id);
      enqueueSnackbar('Execution retried', { variant: 'success' });
      closeDetails();
      loadExecutions();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to retry', { variant: 'error' });
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading && executions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Workflow Executions</Typography>
        <Alert severity="warning">You do not have permission to view executions.</Alert>
      </Box>
    );
  }

  const activeExec = selectedExecution;
  const isLive = activeExec && ['pending', 'running'].includes(activeExec.status);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Workflow Executions</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadExecutions} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(0);
            setSearchQuery(searchInput.trim());
          }}
          style={{ flex: 1 }}
        >
          <TextField
            fullWidth
            placeholder="Search by workflow name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
        </form>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="running">Running</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Workflow</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Started By</TableCell>
              <TableCell>Error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No executions found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : executions.map((execution) => (
              <TableRow key={execution.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {execution.workflow_name || execution.workflow || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell><StatusChip status={execution.status} /></TableCell>
                <TableCell>{new Date(execution.started_at).toLocaleString()}</TableCell>
                <TableCell>{formatDuration(execution.duration)}</TableCell>
                <TableCell>{execution.started_by_email || '-'}</TableCell>
                <TableCell>
                  {execution.error_message ? (
                    <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 180 }}>
                      {execution.error_message}
                    </Typography>
                  ) : '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetails(execution)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {execution.status === 'failed' && (
                    <Tooltip title="Retry">
                      <span>
                        <IconButton size="small" onClick={() => handleRetry(execution)} disabled={!canCancel}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {['running', 'pending'].includes(execution.status) && (
                    <Tooltip title="Cancel">
                      <span>
                        <IconButton size="small" onClick={() => handleCancel(execution)} disabled={!canCancel}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      {/* ── Detail Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={detailDialogOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Execution Details
          {isLive && (
            <Chip
              label="Live"
              color="info"
              size="small"
              sx={{ ml: 1, animation: 'pulse 1.5s infinite' }}
            />
          )}
        </DialogTitle>

        {isLive && <LinearProgress />}

        <DialogContent dividers>
          {detailsLoading && !activeExec ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : activeExec ? (
            <>
              {/* Summary */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Workflow</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeExec.workflow_name || activeExec.workflow}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}><StatusChip status={activeExec.status} /></Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <Typography variant="body2">{new Date(activeExec.started_at).toLocaleString()}</Typography>
                </Box>
                {activeExec.completed_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                    <Typography variant="body2">{new Date(activeExec.completed_at).toLocaleString()}</Typography>
                  </Box>
                )}
                {activeExec.duration && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body2">{formatDuration(activeExec.duration)}</Typography>
                  </Box>
                )}
              </Box>

              {/* Pending / Running banners */}
              {activeExec.status === 'pending' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This execution is waiting to be picked up by a worker. It will start within 30 seconds.
                  If it has been stuck for a long time, cancel it and run the workflow again.
                </Alert>
              )}
              {activeExec.status === 'running' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Workflow is currently executing. Node results will appear below as they complete.
                  Auto-refreshing every 3 seconds…
                </Alert>
              )}

              {/* Error */}
              {activeExec.error_message && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {activeExec.error_message}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Node Executions */}
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Node Executions ({selectedNodeExecutions.length})
              </Typography>

              {detailsLoading ? (
                <Box display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </Box>
              ) : selectedNodeExecutions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {['pending', 'running'].includes(activeExec.status)
                    ? 'No node executions yet — waiting for workflow to start…'
                    : 'No node executions recorded for this execution.'}
                </Typography>
              ) : (
                selectedNodeExecutions.map((nodeExec) => (
                  <Accordion key={nodeExec.id} disableGutters sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <StatusChip status={nodeExec.status} />
                        <Typography variant="body2" fontWeight="medium">
                          {nodeExec.node_name || nodeExec.node}
                        </Typography>
                        {nodeExec.node_type && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>
                            {nodeExec.node_type}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {nodeExec.error_message && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {nodeExec.error_message}
                        </Alert>
                      )}
                      <OutputBlock data={(nodeExec as any).input_data} label="Input" />
                      <OutputBlock data={nodeExec.output_data} label="Output" />
                    </AccordionDetails>
                  </Accordion>
                ))
              )}

              {/* Full result data (available after completion) */}
              {(activeExec as any).result_data && Object.keys((activeExec as any).result_data).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Final Result Data</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <OutputBlock data={(activeExec as any).result_data} label="" />
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </>
          ) : null}
        </DialogContent>

        <DialogActions>
          {activeExec && activeExec.status === 'failed' && (
            <Button
              startIcon={<ReplayIcon />}
              onClick={() => handleRetry(activeExec)}
              disabled={!canCancel}
              color="primary"
            >
              Retry
            </Button>
          )}
          {activeExec && ['pending', 'running'].includes(activeExec.status) && (
            <Button
              startIcon={<CancelIcon />}
              onClick={() => handleCancel(activeExec)}
              disabled={!canCancel}
              color="warning"
            >
              Cancel Execution
            </Button>
          )}
          <Button onClick={closeDetails} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
